import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import { Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Assignment from "@material-ui/icons/Assignment";
import * as React from "react";
import { RouterProps } from "react-router";
import Component from "../../components/Component";
import MobilePage from "../../components/MobilePage";
import withAxios, { IAxiosProps } from "../../components/withAxios";
import { ResponseSource } from "src/services";
import { IRespondentPagePassedDown } from "../RespondentPage";
import { Box } from "@material-ui/core";

const styles = (theme: Theme): Record<string, CSSProperties> => ({
  coachName: {
    fontSize: "22px",
  },
});

interface INameInputPageProps {
  code: string;
  respondentNameChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  respondentName: string;
  coachId?: number;
  forVideoMidSeson: boolean;
  schoolData:any;
}

type IProps = INameInputPageProps &
  RouterProps &
  IRespondentPagePassedDown &
  IAxiosProps &
  WithStyles<keyof ReturnType<typeof styles>>;

interface IInputInformationState {
  selectedCoachId?: number;
}

export default withStyles(styles)(
  withAxios(
    class NameInputPage extends Component<IProps, IInputInformationState> {
      public readonly state = this.buildState({
        selectedCoachId: undefined,
      });

      public componentDidMount() {
        const { code, loadSurvey } = this.props;
        loadSurvey(code);
      }

      public componentDidUpdate(prevProps: IProps) {
        const { surveyResponse, responseSource, history, coachId, code } =
          this.props;

        // check if a coach is in the coachs list. If not then send the user back
        // This should only happen if a user is foolish enough to type in the url the coachId
        // or if the survey is finished/not valid
        if (
          surveyResponse !== undefined &&
          !surveyResponse.coaches.find((c) => c.id === coachId)
        ) {
          const responsePath =
            responseSource === ResponseSource.coach
              ? "teacher"
              : ResponseSource[responseSource];
          history.push(`/${responsePath}/${code}`);
        }
      }

      // render for both students and coaches
      public render() {
        const {
          responseSource,
          surveyResponse,
          school,
          loadingSurvey,
          coachId,
        } = this.props;
        if (surveyResponse === undefined) {
          if (loadingSurvey) {
            return <LinearProgress variant="query" />;
          }
          return <h1>Oops… something went wrong. Survey not loaded.</h1>;
        }

        const coach = surveyResponse.coaches.find((c) => c.id === coachId);
        if (coach === undefined) {
          return <h1>Oops… something went wrong. {this.props?.schoolData?.isAdSurvey ? "AD" :"Teacher"} was not found.</h1>;
        }

        const sportName = (() => {
          if (
            surveyResponse === undefined ||
            surveyResponse.sport === undefined
          ) {
            return ``;
          } else {
            return `${surveyResponse.sport.name}`;
          }
        })();
        
        switch (responseSource) {
          case ResponseSource.student:
            return (
              <MobilePage
                header={sportName}
                title={`${this.props?.schoolData?.isAdSurvey ? "AD:" : "Teacher:"} ${coach.name}`}
                subtitle=""
                schoolLogo={school.logo1}
                schoolLogo2={school.logo2}
                schoolName={school.name}
                headerIcon={<Assignment />}
                tagline={`${school.tagline}`}
              >
                {this.renderNameChange()}
              </MobilePage>
            );
          case ResponseSource.coach:
            return (
              <div>
                <span> {this.props?.schoolData?.isAdSurvey ? "AD's" :"Teachers"} do not need this page</span>
              </div>
            );
          case ResponseSource.parent:
            return (
              <MobilePage
                header={sportName}
                title={`${this.props?.schoolData?.isAdSurvey ? "AD:" : "Teacher:"} ${coach.name}`}
                subtitle=""
                schoolLogo={school.logo1}
                schoolLogo2={school.logo2}
                schoolName={school.name}
                headerIcon={<Assignment />}
                tagline={`${school.tagline}`}
              >
                {this.renderNameChange()}
              </MobilePage>
            );
          default:
            return <span>Something is wrong</span>;
        }
      }

      private renderNameChange() {
        const { respondentNameChange, respondentName, responseSource, forVideoMidSeson, schoolData } =
          this.props;
        const confidentialityMessage = (() => {
          switch (responseSource) {
            case ResponseSource.student:
              return forVideoMidSeson ? `This survey gives you a direct connection to your teacher, who will see your individual responses. They want your honest input to understand how you feel and how to better support you and the team. By sharing openly and honestly, we can work together to build something better, together.`
              : `Your feedback is important to us. Please take a moment to thoughtfully answer the following questions so we can better understand your perspective.`;
            case ResponseSource.parent:
              return forVideoMidSeson ? `Parents, this survey allows you to provide feedback directly to your teacher. Just like with your child, growth happens through encouragement and honesty. Your teacher will see your responses, so be respectful yet truthful—just as you would want your child to be treated with care and guided with honesty.` :
              schoolData?.isAdSurvey ? `As we work to strengthen our admin program and culture, your voice is incredibly important. Including your name helps us better understand feedback, follow up when needed, and celebrate positive contributions.
Anonymous responses make it difficult to take meaningful action, and for this process to help us grow, we need to know who’s sharing what. Surveys without a name may not be considered valid.
Thank you for partnering with us to build a stronger, more connected admin community.`:
              `Your name and answers will be seen by the admin department. Your name and answers will be held confidential from the ${this.props?.schoolData?.isAdSurvey ? "AD" :"teacher"}. All written answers will be searched for themes and will not be quoted verbatim. Your answers will remain confidential from ${this.props?.schoolData?.isAdSurvey ? "AD's" :"Teachers"}, but the admin department will have option to see the name associated with your answer.`;
            default:
              return `Oops… you should not be seeing this page. Please report this issue to an administrator.`;
          }
        })();
        return (
          <div>
            <Typography>{confidentialityMessage}</Typography>
            <div style={{ paddingTop: "20px" }}>First and Last Name</div>
            <form onSubmit={this.submitName}>
              <TextField
                onChange={respondentNameChange}
                value={respondentName}
              />
              <Box marginTop="2rem">
                <div className="submit-btn-primary">
                <Button
                  type="submit"
                  disabled={respondentName === ""}
                  variant="contained"
                  color="primary"
                >
                  Submit Name
                </Button>
                </div>
              </Box>
            </form>
          </div>
        );
      }

      private submitName = async (e: React.FormEvent) => {
        e.preventDefault();
        const { responseSource, code, coachId } = this.props;
        const responsePath =
          responseSource === ResponseSource.coach
            ? "teacher"
            : ResponseSource[responseSource];
        this.props.history.push(
          `/${responsePath}/${code}/${coachId}/${0}`
        );
      };
    }
  )
);



// WEBPACK FOOTER //
// ./src/pages/respondent/NameInputPage.tsx